/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react';
import { getDownloadURL, ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from './firebaseConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Card,
  CardContent,
  Box,
  Container,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
  Snackbar,
  AppBar,
  Toolbar,
  Drawer,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Receipt,
  FilterList,
  Download,
  Refresh,
  Logout,
  Visibility,
  CheckCircle,
  HourglassEmpty,
  Category,
  Business,
  Close,
  Email,
  AttachFile,
  ArrowDropDown,
  Search,
  TrendingUp,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { auth, db } from './firebaseConfig';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTimestamp = (ts) => {
  if (!ts) return '-';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (amount) => {
  const n = parseFloat(amount);
  return isNaN(n) ? '0,00' : n.toFixed(2).replace('.', ',');
};

const isPaidEntry = (entry) => {
  if (!entry) return false;
  return entry.paymentType === 'paid' || (entry.paymentType === undefined && entry.iban);
};

const statusLabel = (s) => (s === 'pending' ? 'Offen' : s === 'accepted' ? 'Angenommen' : s === 'rejected' ? 'Abgelehnt' : '–');
const statusColor = (s) => (s === 'accepted' ? 'success' : s === 'rejected' ? 'error' : 'warning');

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg', 'avif', 'tiff', 'tif'];

const detectFileKind = (entry) => {
  const candidates = [entry?.fileName, entry?.filePath, entry?.fileUrl].filter(Boolean);
  for (const raw of candidates) {
    const lower = raw.toLowerCase();
    // Strip query string for URLs
    const path = lower.split('?')[0];
    const ext = path.split('.').pop();
    if (IMAGE_EXTS.includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
  }
  // Mime type from upload (if stored)
  const mt = (entry?.fileType || '').toLowerCase();
  if (mt.startsWith('image/')) return 'image';
  if (mt === 'application/pdf') return 'pdf';
  return 'unknown';
};

// ---------------------------------------------------------------------------
// Reusable building blocks
// ---------------------------------------------------------------------------

const KpiCard = ({ icon, label, value, sub, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette[color].main, 0.12),
            color: `${color}.main`,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const StatBar = ({ label, count, amount, percent, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2,
      py: 1.25,
      borderRadius: 2,
      cursor: 'pointer',
      transition: 'background 0.15s',
      '&:hover': { backgroundColor: 'background.subtle' },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
      <Typography variant="body2" fontWeight={600} color="text.primary" noWrap sx={{ flex: 1, mr: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} color="primary.main">
        {formatAmount(amount)} €
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{ flex: 1, height: 6, borderRadius: 999 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
        {count} {count === 1 ? 'Beleg' : 'Belege'}
      </Typography>
    </Box>
  </Box>
);

const StatusPill = ({ status }) => (
  <Chip
    size="small"
    label={statusLabel(status)}
    color={statusColor(status)}
    sx={{ fontWeight: 600 }}
  />
);

const PaymentPill = ({ paid }) => (
  <Chip
    size="small"
    variant={paid ? 'filled' : 'outlined'}
    label={paid ? 'Überwiesen' : 'Ausstehend'}
    color={paid ? 'success' : 'default'}
    sx={{ fontWeight: 600 }}
  />
);

// ---------------------------------------------------------------------------
// Detail Drawer
// ---------------------------------------------------------------------------

function generateEpcQrCodeContent({ name, iban, bic, amount, purpose }) {
  const sanitized = String(amount).replace(',', '.');
  const safe = parseFloat(sanitized);
  if (isNaN(safe)) return '';
  return `BCD
001
1
SCT
${bic}
${name}
${iban}
EUR${safe}
CHAR

ERSTATTUNG AUSLAGE KFG:${purpose}
Kulturfestival QR-Code`;
}

const DetailDrawer = ({ entry, onClose, onUpdateStatus, onTogglePaid, rejectReason, setRejectReason }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [previewMode, setPreviewMode] = useState('auto');

  useEffect(() => {
    setPreviewMode('auto');
  }, [entry?.id]);

  if (!entry) return null;
  const detected = detectFileKind(entry);
  // 'auto' starts as detected; on image error we fall back to iframe
  const effectiveKind = previewMode === 'fallback' ? 'pdf' : detected === 'unknown' ? 'image' : detected;
  const paidEntry = isPaidEntry(entry);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={!!entry}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 540 },
          maxWidth: '100%',
          height: { xs: '95vh', md: '100%' },
          borderRadius: { xs: '20px 20px 0 0', md: 0 },
          bgcolor: 'background.default',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main' }}>
            <Receipt />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {entry.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatAmount(entry.amount)} € · {entry.date}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Schließen">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        <Stack spacing={2.5}>
          {/* Status row */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <StatusPill status={entry.status} />
            <PaymentPill paid={entry.paid} />
            {!paidEntry && (
              <Chip size="small" label="An Empfänger zu zahlen" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
          </Box>

          {/* File preview */}
          {entry.fileUrl && (
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Belegdatei
                </Typography>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.subtle',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {effectiveKind === 'image' ? (
                    <img
                      src={entry.fileUrl}
                      alt=""
                      onError={() => setPreviewMode('fallback')}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : effectiveKind === 'pdf' ? (
                    <iframe
                      src={entry.fileUrl}
                      title="Belegvorschau"
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  ) : (
                    <AttachFile sx={{ fontSize: 48, color: 'text.secondary' }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {entry.fileName}
                  </Typography>
                  <Button size="small" href={entry.fileUrl} target="_blank" rel="noopener noreferrer">
                    Im Tab öffnen
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Beleg details */}
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Belegdaten
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Betrag" value={`${formatAmount(entry.amount)} €`} bold />
                <DetailRow label="Belegdatum" value={entry.date || '–'} />
                <DetailRow label="Bereich" value={entry.area || '–'} />
                <DetailRow label="Kategorie" value={entry.category || '–'} />
                <DetailRow label="Zweck" value={entry.purpose || '–'} multiline />
                <DetailRow
                  label="Bezahlart"
                  value={paidEntry ? 'Bereits bezahlt (Rückerstattung)' : 'Noch zu bezahlen (an Empfänger)'}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Contact / banking */}
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Kontakt
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Name" value={entry.name} />
                {entry.email && (
                  <DetailRow
                    label="E-Mail"
                    value={
                      <Button
                        size="small"
                        startIcon={<Email fontSize="small" />}
                        href={(() => {
                          const subject = `Beleg: ${entry.purpose || 'Rückfrage'}`;
                          const body =
                            entry.status === 'rejected' && entry.rejectReason
                              ? `Dein Beleg wurde abgelehnt. Der Grund lautet: ${entry.rejectReason}`
                              : '';
                          return `mailto:${entry.email}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`;
                        })()}
                        sx={{ p: 0, minHeight: 0, justifyContent: 'flex-start' }}
                      >
                        {entry.email}
                      </Button>
                    }
                  />
                )}
                {paidEntry && (
                  <>
                    <DetailRow label="IBAN" value={entry.iban} mono />
                    {entry.bic && <DetailRow label="BIC" value={entry.bic} mono />}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* QR + payment */}
          {entry.status === 'accepted' && (
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                {paidEntry ? (
                  <>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Überweisung
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.subtle',
                      }}
                    >
                      <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1.5 }}>
                        <QRCodeSVG
                          value={generateEpcQrCodeContent({
                            name: entry.name,
                            iban: entry.iban,
                            bic: entry.bic || '',
                            amount: entry.amount,
                            purpose: entry.purpose || 'Belegzahlung',
                          })}
                          size={120}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          EPC QR-Code
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Mit Banking-App scannen für die Überweisung.
                        </Typography>
                        <Button
                          variant={entry.paid ? 'outlined' : 'contained'}
                          color={entry.paid ? 'warning' : 'success'}
                          onClick={() => onTogglePaid(entry.id, entry.paid)}
                          size="small"
                        >
                          Als {entry.paid ? 'nicht überwiesen' : 'überwiesen'} markieren
                        </Button>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Dieser Beleg muss an den Rechnungsempfänger überwiesen werden.
                    </Alert>
                    <Button
                      variant={entry.paid ? 'outlined' : 'contained'}
                      color={entry.paid ? 'warning' : 'success'}
                      onClick={() => onTogglePaid(entry.id, entry.paid)}
                    >
                      Als {entry.paid ? 'nicht überwiesen' : 'überwiesen'} markieren
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status change */}
          {!entry.paid && (
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Prüfung
                </Typography>
                {(entry.status === 'pending' || entry.status === 'accepted') && (
                  <TextField
                    label="Ablehnungsgrund (nur bei Ablehnung)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => onUpdateStatus(entry.id, 'accepted')}
                    fullWidth
                    startIcon={<CheckCircle />}
                  >
                    Annehmen
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => onUpdateStatus(entry.id, 'rejected', rejectReason)}
                    disabled={!rejectReason.trim()}
                    fullWidth
                    startIcon={<Close />}
                  >
                    Ablehnen
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {entry.status === 'rejected' && entry.rejectReason && (
            <Alert severity="error">
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Ablehnungsgrund
              </Typography>
              <Typography variant="body2">{entry.rejectReason}</Typography>
            </Alert>
          )}

          {/* Meta */}
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Meta
              </Typography>
              <Stack spacing={0.75}>
                <DetailRow label="Eingereicht am" value={formatTimestamp(entry.submittedAt)} small />
                {entry.fileName && <DetailRow label="Dateiname" value={entry.fileName} small />}
                {entry.clientIp && <DetailRow label="IP" value={entry.clientIp} small mono />}
                {entry.id && <DetailRow label="ID" value={entry.id} small mono />}
                {entry.userAgent && (
                  <DetailRow label="User-Agent" value={entry.userAgent} small mono multiline />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Drawer>
  );
};

const DetailRow = ({ label, value, bold, mono, multiline, small }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: multiline ? 'flex-start' : 'baseline' }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width: 100, flexShrink: 0, fontSize: small ? 11 : 12, pt: multiline ? 0.5 : 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: bold ? 700 : 500,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
        wordBreak: 'break-all',
        fontSize: small ? 12 : 14,
        color: small ? 'text.secondary' : 'text.primary',
        flex: 1,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// AdminPanel
// ---------------------------------------------------------------------------

function AdminPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [statisticsPopup, setStatisticsPopup] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(true);
  const [exportMenu, setExportMenu] = useState(null);
  const [zipProgress, setZipProgress] = useState(null);

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  // ----- Auth check -----
  useEffect(() => {
    const check = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      try {
        const ref = doc(db, 'user', auth.currentUser.uid);
        const snap = await getDoc(ref);
        if (!snap.exists() || snap.data()?.role !== 'admin') {
          navigate('/login');
        }
      } catch {
        navigate('/login');
      }
    };
    check();
  }, [navigate]);

  // ----- Data -----
  const deduplicate = (data) => {
    const uniqueById = data.filter((item, i, arr) => i === arr.findIndex((x) => x.id === item.id));
    const seen = new Set();
    return uniqueById.filter((item) => {
      const key = `${item.name}|${item.amount}|${item.date}|${item.paymentType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'submissions'));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSubmissions(deduplicate(data));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch {}
  };

  const updateStatus = async (id, newStatus, reason = '') => {
    try {
      const ref = doc(db, 'submissions', id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        showSnackbar('Dokument nicht gefunden. Daten werden aktualisiert.');
        await refreshData();
        return;
      }
      await updateDoc(ref, { status: newStatus, rejectReason: reason || '', updatedAt: new Date() });
      setSubmissions((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: newStatus, rejectReason: reason } : e))
      );
      setSelected((p) => (p ? { ...p, status: newStatus, rejectReason: reason } : null));
      setRejectReason('');
      showSnackbar(`Beleg wurde als ${statusLabel(newStatus).toLowerCase()} markiert.`, 'success');
    } catch {
      showSnackbar('Fehler beim Aktualisieren. Daten werden aktualisiert.');
      await refreshData();
    }
  };

  const togglePaid = async (id, currentPaid) => {
    try {
      const ref = doc(db, 'submissions', id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        showSnackbar('Dokument nicht gefunden. Daten werden aktualisiert.');
        await refreshData();
        return;
      }
      await updateDoc(ref, { paid: !currentPaid, updatedAt: new Date() });
      setSubmissions((prev) => prev.map((e) => (e.id === id ? { ...e, paid: !currentPaid } : e)));
      setSelected((p) => (p ? { ...p, paid: !currentPaid } : null));
    } catch {
      showSnackbar('Fehler beim Aktualisieren. Daten werden aktualisiert.');
      await refreshData();
    }
  };

  const handleOpenDetails = async (entry) => {
    try {
      const fileRef = storageRef(storage, entry.filePath);
      const url = await getDownloadURL(fileRef);
      setSelected({ ...entry, fileUrl: url });
    } catch {
      setSelected({ ...entry, fileUrl: null });
    }
  };

  // ----- Stats -----
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const filtered = submissions.filter((e) => {
      if (dateFilter === 'all') return true;
      if (!e.date) return false;
      const d = new Date(e.date);
      if (dateFilter === 'today') return d >= today;
      if (dateFilter === 'week') return d >= thisWeek;
      if (dateFilter === 'month') return d >= thisMonth;
      return true;
    });

    const totalAmount = filtered.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const paidAmount = filtered.filter((e) => e.paid).reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const unpaidAmount = filtered
      .filter((e) => !e.paid && e.status === 'accepted')
      .reduce((s, e) => s + parseFloat(e.amount || 0), 0);

    const categoryStats = {};
    const areaStats = {};
    filtered.forEach((e) => {
      const a = parseFloat(e.amount || 0);
      const cat = e.category || 'Nicht zugeordnet';
      categoryStats[cat] = categoryStats[cat] || { count: 0, amount: 0 };
      categoryStats[cat].count++;
      categoryStats[cat].amount += a;
      const ar = e.area || 'Nicht zugeordnet';
      areaStats[ar] = areaStats[ar] || { count: 0, amount: 0 };
      areaStats[ar].count++;
      areaStats[ar].amount += a;
    });

    return {
      total: filtered.length,
      pending: filtered.filter((e) => e.status === 'pending').length,
      accepted: filtered.filter((e) => e.status === 'accepted').length,
      rejected: filtered.filter((e) => e.status === 'rejected').length,
      paid: filtered.filter((e) => e.paid).length,
      unpaid: filtered.filter((e) => !e.paid && e.status === 'accepted').length,
      totalAmount,
      paidAmount,
      unpaidAmount,
      categoryStats,
      areaStats,
      _filtered: filtered,
    };
  }, [submissions, dateFilter]);

  const getSubmissionsForCategory = (category) =>
    (stats._filtered || submissions).filter((e) => (e.category || 'Nicht zugeordnet') === category);
  const getSubmissionsForArea = (area) =>
    (stats._filtered || submissions).filter((e) => (e.area || 'Nicht zugeordnet') === area);

  // ----- Filter -----
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((entry) => {
      if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
      if (paymentFilter === 'paid' && !entry.paid) return false;
      if (paymentFilter === 'unpaid' && entry.paid) return false;

      if (dateFilter !== 'all' && entry.date) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const week = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const month = new Date(now.getFullYear(), now.getMonth(), 1);
        const d = new Date(entry.date);
        if (dateFilter === 'today' && d < today) return false;
        if (dateFilter === 'week' && d < week) return false;
        if (dateFilter === 'month' && d < month) return false;
      } else if (dateFilter !== 'all' && !entry.date) {
        return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const hay = [entry.name, entry.purpose, entry.area, entry.category, entry.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [submissions, statusFilter, paymentFilter, dateFilter, searchQuery]);

  // ----- Export -----
  const exportToCSV = (list = filteredSubmissions, filenameSuffix = '') => {
    const data = list.map((e) => ({
      ID: e.id || '',
      Name: e.name,
      Email: e.email || '',
      IBAN: e.iban || '',
      BIC: e.bic || '',
      Betrag: e.amount,
      Zweck: e.purpose || '',
      Bereich: e.area || '',
      Kategorie: e.category || '',
      Belegdatum: e.date || '',
      Status: e.status,
      Überwiesen: e.paid ? 'Ja' : 'Nein',
      Ablehnungsgrund: e.rejectReason || '',
      Eingereicht_am: formatTimestamp(e.submittedAt),
      Dateiname: e.fileName || '',
      IP_Adresse: e.clientIp || '',
      User_Agent: e.userAgent || '',
    }));
    const csv = Papa.unparse(data);
    const BOM = '﻿';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const suffix = filenameSuffix ? `-${filenameSuffix}` : '';
    saveAs(blob, `festival-belege${suffix}-${new Date().toISOString().split('T')[0]}.csv`);
    setExportMenu(null);
  };

  const downloadAllFilesAsZip = async (list = filteredSubmissions, filenameSuffix = '') => {
    setExportMenu(null);
    try {
      const withFiles = list.filter((e) => e.filePath);
      if (!withFiles.length) {
        showSnackbar('Keine Dateien zum Download gefunden.');
        return;
      }
      const zip = new JSZip();
      let processed = 0;
      setZipProgress({ processed, total: withFiles.length });

      for (const entry of withFiles) {
        try {
          const ref = storageRef(storage, entry.filePath);
          const blob = await getBlob(ref);
          if (blob.size === 0) {
            processed++;
            setZipProgress({ processed, total: withFiles.length });
            continue;
          }
          const recipient = entry.name || 'Unbekannt';
          const purpose = entry.purpose || 'Zweck';
          const cleanRecipient = recipient.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
          const cleanPurpose = purpose.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
          let ext = '';
          if (entry.filePath?.includes('.')) ext = entry.filePath.split('.').pop();
          else if (blob.type) {
            if (blob.type.includes('pdf')) ext = 'pdf';
            else if (blob.type.includes('jpeg')) ext = 'jpg';
            else if (blob.type.includes('png')) ext = 'png';
            else if (blob.type.includes('gif')) ext = 'gif';
            else if (blob.type.includes('image')) ext = 'jpg';
          }
          if (!ext) ext = 'bin';
          zip.file(`${cleanRecipient}_${cleanPurpose}.${ext}`, blob);
        } catch {}
        processed++;
        setZipProgress({ processed, total: withFiles.length });
      }

      if (Object.keys(zip.files).length === 0) {
        showSnackbar('Keine Dateien konnten zur ZIP hinzugefügt werden.');
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const suffix = filenameSuffix ? `-${filenameSuffix}` : '';
      saveAs(zipBlob, `festival-belege${suffix}-${new Date().toISOString().split('T')[0]}.zip`);
    } catch {
      showSnackbar('Fehler beim Erstellen der ZIP-Datei.');
    } finally {
      setZipProgress(null);
    }
  };

  const maxCatAmount = Math.max(1, ...Object.values(stats.categoryStats).map((s) => s.amount));
  const maxAreaAmount = Math.max(1, ...Object.values(stats.areaStats).map((s) => s.amount));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <Receipt fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              Belegverwaltung
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                Admin-Dashboard
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={refreshData} disabled={loading} aria-label="Aktualisieren">
                {loading ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              onClick={(e) => setExportMenu(e.currentTarget)}
              startIcon={!isMobile ? <Download /> : null}
              endIcon={<ArrowDropDown />}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenu}
              open={!!exportMenu}
              onClose={() => setExportMenu(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 220 } } }}
            >
              <MenuItem onClick={exportToCSV}>
                <Download fontSize="small" sx={{ mr: 1.5 }} /> CSV exportieren
              </MenuItem>
              <MenuItem onClick={downloadAllFilesAsZip} disabled={!!zipProgress}>
                <AttachFile fontSize="small" sx={{ mr: 1.5 }} />
                {zipProgress ? `ZIP wird erstellt (${zipProgress.processed}/${zipProgress.total})` : 'Belege als ZIP'}
              </MenuItem>
            </Menu>
            <Tooltip title="Abmelden">
              <IconButton onClick={handleLogout} aria-label="Abmelden" color="error">
                <Logout />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
        {zipProgress && (
          <LinearProgress
            variant="determinate"
            value={zipProgress.total ? (zipProgress.processed / zipProgress.total) * 100 : 0}
          />
        )}
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Stack spacing={3}>
          {/* Page header */}
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              Übersicht
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Alle eingereichten Belege auf einen Blick.
            </Typography>
          </Box>

          {/* KPI Grid */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            }}
          >
            <KpiCard
              icon={<Receipt fontSize="small" />}
              label="Gesamt"
              value={stats.total}
              sub={`${formatAmount(stats.totalAmount)} €`}
              color="primary"
            />
            <KpiCard
              icon={<HourglassEmpty fontSize="small" />}
              label="Offen"
              value={stats.pending}
              sub="Warten auf Prüfung"
              color="warning"
            />
            <KpiCard
              icon={<CheckCircle fontSize="small" />}
              label="Überwiesen"
              value={`${formatAmount(stats.paidAmount)} €`}
              sub={`${stats.paid} ${stats.paid === 1 ? 'Beleg' : 'Belege'}`}
              color="success"
            />
            <KpiCard
              icon={<TrendingUp fontSize="small" />}
              label="Offen (€)"
              value={`${formatAmount(stats.unpaidAmount)} €`}
              sub={`${stats.unpaid} ${stats.unpaid === 1 ? 'Beleg' : 'Belege'}`}
              color="info"
            />
          </Box>

          {/* Charts row */}
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Category fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Top Kategorien
                  </Typography>
                </Box>
                {Object.keys(stats.categoryStats).length === 0 ? (
                  <EmptyHint label="Noch keine Daten" />
                ) : (
                  <Stack spacing={0.25}>
                    {Object.entries(stats.categoryStats)
                      .sort(([, a], [, b]) => b.amount - a.amount)
                      .slice(0, 6)
                      .map(([k, v]) => (
                        <StatBar
                          key={k}
                          label={k}
                          count={v.count}
                          amount={v.amount}
                          percent={(v.amount / maxCatAmount) * 100}
                          onClick={() =>
                            setStatisticsPopup({ type: 'category', name: k, submissions: getSubmissionsForCategory(k) })
                          }
                        />
                      ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Top Bereiche
                  </Typography>
                </Box>
                {Object.keys(stats.areaStats).length === 0 ? (
                  <EmptyHint label="Noch keine Daten" />
                ) : (
                  <Stack spacing={0.25}>
                    {Object.entries(stats.areaStats)
                      .sort(([, a], [, b]) => b.amount - a.amount)
                      .slice(0, 6)
                      .map(([k, v]) => (
                        <StatBar
                          key={k}
                          label={k}
                          count={v.count}
                          amount={v.amount}
                          percent={(v.amount / maxAreaAmount) * 100}
                          onClick={() =>
                            setStatisticsPopup({ type: 'area', name: k, submissions: getSubmissionsForArea(k) })
                          }
                        />
                      ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Filters */}
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterList fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Filter
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip size="small" label={`${filteredSubmissions.length} / ${submissions.length}`} />
              </Box>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  placeholder="Suchen nach Name, Zweck, Bereich, Kategorie, E-Mail …"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  size="small"
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FilterGroup
                    label="Status"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { v: 'all', l: 'Alle' },
                      { v: 'pending', l: 'Offen' },
                      { v: 'accepted', l: 'Angenommen' },
                      { v: 'rejected', l: 'Abgelehnt' },
                    ]}
                  />
                  <FilterGroup
                    label="Zahlung"
                    value={paymentFilter}
                    onChange={setPaymentFilter}
                    options={[
                      { v: 'all', l: 'Alle' },
                      { v: 'paid', l: 'Überwiesen' },
                      { v: 'unpaid', l: 'Ausstehend' },
                    ]}
                  />
                  <FilterGroup
                    label="Zeitraum"
                    value={dateFilter}
                    onChange={setDateFilter}
                    options={[
                      { v: 'all', l: 'Alle' },
                      { v: 'today', l: 'Heute' },
                      { v: 'week', l: '7 Tage' },
                      { v: 'month', l: 'Monat' },
                    ]}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Submissions list */}
          {loading && submissions.length === 0 ? (
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </CardContent>
            </Card>
          ) : filteredSubmissions.length === 0 ? (
            <Card>
              <CardContent sx={{ p: 6, textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Keine Belege gefunden
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Probiere andere Filter oder die Suche.
                </Typography>
              </CardContent>
            </Card>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {filteredSubmissions.map((entry) => (
                <SubmissionCard key={entry.id} entry={entry} onClick={() => handleOpenDetails(entry)} />
              ))}
            </Stack>
          ) : (
            <Card sx={{ overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.subtle' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bereich</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Beschreibung</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Betrag</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Datum</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Eingereicht</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Zahlung</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubmissions.map((entry) => (
                      <TableRow
                        key={entry.id}
                        hover
                        onClick={() => handleOpenDetails(entry)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {entry.name}
                          </Typography>
                          {entry.email && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {entry.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.area ? (
                            <Chip size="small" label={entry.area} variant="outlined" />
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="body2" noWrap>
                            {entry.purpose || '–'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {formatAmount(entry.amount)} €
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {entry.date || '–'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatTimestamp(entry.submittedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusPill status={entry.status} />
                        </TableCell>
                        <TableCell>
                          <PaymentPill paid={entry.paid} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Stack>
      </Container>

      {/* Detail Drawer */}
      <DetailDrawer
        entry={selected}
        onClose={() => setSelected(null)}
        onUpdateStatus={updateStatus}
        onTogglePaid={togglePaid}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />

      {/* Statistics Drilldown */}
      <Dialog
        open={!!statisticsPopup}
        onClose={() => setStatisticsPopup(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 5 } }}
      >
        {(() => {
          const list = statisticsPopup?.submissions || [];
          const total = list.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
          const paidAmt = list.filter((e) => e.paid).reduce((s, e) => s + parseFloat(e.amount || 0), 0);
          const unpaidAmt = list
            .filter((e) => !e.paid && e.status === 'accepted')
            .reduce((s, e) => s + parseFloat(e.amount || 0), 0);
          const pendingCount = list.filter((e) => e.status === 'pending').length;
          const acceptedCount = list.filter((e) => e.status === 'accepted').length;
          const rejectedCount = list.filter((e) => e.status === 'rejected').length;
          const filenameSuffix = (statisticsPopup?.name || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
          const withFilesCount = list.filter((e) => e.filePath).length;

          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
                {statisticsPopup?.type === 'category' ? (
                  <Category color="primary" />
                ) : (
                  <Business color="primary" />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {statisticsPopup?.type === 'category' ? 'Kategorie' : 'Bereich'}: {statisticsPopup?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {list.length} {list.length === 1 ? 'Beleg' : 'Belege'} · {formatAmount(total)} €
                  </Typography>
                </Box>
                <IconButton onClick={() => setStatisticsPopup(null)} aria-label="Schließen">
                  <Close />
                </IconButton>
              </DialogTitle>
              <Divider />

              {/* Stats summary + download actions */}
              {list.length > 0 && (
                <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: 'background.subtle' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.5,
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                      mb: 2,
                    }}
                  >
                    <MiniStat label="Gesamt" value={`${formatAmount(total)} €`} sub={`${list.length} Belege`} />
                    <MiniStat
                      label="Überwiesen"
                      value={`${formatAmount(paidAmt)} €`}
                      sub={`${list.filter((e) => e.paid).length} Belege`}
                      color="success"
                    />
                    <MiniStat
                      label="Offen (Betrag)"
                      value={`${formatAmount(unpaidAmt)} €`}
                      sub="Angenommen, unbezahlt"
                      color="warning"
                    />
                    <MiniStat
                      label="Status"
                      value={`${pendingCount} / ${acceptedCount} / ${rejectedCount}`}
                      sub="Offen / OK / Abgel."
                    />
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Download />}
                      onClick={() => exportToCSV(list, filenameSuffix)}
                    >
                      CSV exportieren
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFile />}
                      disabled={!withFilesCount || !!zipProgress}
                      onClick={() => downloadAllFilesAsZip(list, filenameSuffix)}
                    >
                      {zipProgress
                        ? `ZIP (${zipProgress.processed}/${zipProgress.total})`
                        : `Belege als ZIP (${withFilesCount})`}
                    </Button>
                  </Stack>
                </Box>
              )}

              <DialogContent sx={{ p: 0 }}>
                {list.length ? (
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: 'background.subtle' } }}>
                          <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Betrag</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Datum</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Zweck</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Zahlung</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {list.map((s) => (
                          <TableRow
                            key={s.id}
                            hover
                            onClick={() => {
                              setStatisticsPopup(null);
                              handleOpenDetails(s);
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={700} color="primary.main">
                                {formatAmount(s.amount)} €
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {s.date ? new Date(s.date).toLocaleDateString('de-DE') : '–'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography variant="body2" noWrap>
                                {s.purpose || '–'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusPill status={s.status} />
                            </TableCell>
                            <TableCell>
                              <PaymentPill paid={s.paid} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Keine Belege gefunden.
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const FilterGroup = ({ label, value, onChange, options }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v) => v && onChange(v)}
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 500,
          px: 1.5,
          borderColor: 'divider',
          color: 'text.secondary',
          '&.Mui-selected': {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.15) },
          },
        },
      }}
    >
      {options.map((o) => (
        <ToggleButton key={o.v} value={o.v}>
          {o.l}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

const SubmissionCard = ({ entry, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      transition: 'transform 0.1s, border-color 0.15s',
      '&:active': { transform: 'scale(0.99)' },
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={700} noWrap>
            {entry.name}
          </Typography>
          {entry.purpose && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {entry.purpose}
            </Typography>
          )}
        </Box>
        <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
          {formatAmount(entry.amount)} €
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <StatusPill status={entry.status} />
        <PaymentPill paid={entry.paid} />
        {entry.area && <Chip size="small" label={entry.area} variant="outlined" />}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {formatTimestamp(entry.submittedAt)}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const MiniStat = ({ label, value, sub, color = 'primary' }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: 10 }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} color={`${color}.main`} sx={{ lineHeight: 1.2, mt: 0.25 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const EmptyHint = ({ label }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default AdminPanel;
